/** Contracts for the disposable combined task schedule; no new approval or execution state is stored. */
import { EntityViewFilter, EntityViewSort } from '../../entities/views';
import { UpcomingWork } from '../service.plan/upcoming.work';
import { ServicePlanStatusSystemName } from '../service.plan/service.plan.status.system.name';
import { TaskApprovalStatus } from './task.approval';
import { Entity } from '../../entity';
import { ServiceHistoryRequest, ServiceHistoryRow } from './visit.service.history';
import { VisitBrowseRow, VisitBrowseTask } from './visit.browse';

/** A task occurrence is scheduled only when an executable Job exists, independently of its resolution. */
export type WorkTaskScheduleState = 'Planned' | 'Scheduled';

/** The experimental projection keeps every Site zone, while the existing Upcoming board keeps its first-zone cell. */
export interface WorkTaskScheduleForecast extends UpcomingWork {
    siteZones?: Entity[];
}

/** One occurrence combines the forecast's planning fields with the stored task's execution facts. */
export interface WorkTaskScheduleRow extends ServiceHistoryRow {
    occurrenceId: string;
    taskId: string;
    scheduleState: WorkTaskScheduleState;
    /** This task's own approval standing (approval grain is task rows); auto-approval is its line's. */
    approvalStatus?: TaskApprovalStatus;
    /**
     * The Visit board row this task's work sits on: its stored Visit id, or the forecast Visit's locator
     * (`projected.…`). It is the id a Visit bulk action names, since approval and movement act per Visit.
     */
    visitLocator?: string;
    /**
     * The task row's id (`buildVisitWorkId` over plan, line, task and scheduled date), the same whether the
     * row is stored yet or still forecast; what a task-level approval or move names.
     */
    visitTaskId?: string;
    /** The plan's status: only a Started plan's work can be approved, moved or withdrawn (plan section 7d). */
    planStatus?: ServicePlanStatusSystemName;
    /** Copied from the forecast: stored work on a day a later plan or line blackout covers, so it needs a reschedule. */
    onBlackout?: true;
}

/** Canonical server-owned parent scope plus the board's narrowing and presentation choices. */
export interface WorkTaskScheduleRequest extends Pick<ServiceHistoryRequest, 'customerId' | 'siteId' | 'servicePlanId'> {
    filters?: EntityViewFilter[];
    search?: string;
    sort?: EntityViewSort;
    timezone?: string;
    page?: number;
    pageSize?: number;
    /** Tenant result segment; applied only after the complete scoped merge and base filters. */
    segmentId?: string;
    /** The Work pane's one task: forecasts are narrowed to this source task before the merge. */
    taskId?: string;
    /** The Work pane's one row (`occurrenceId:taskId`): the merge is narrowed to it before the approval and plan joins. */
    rowId?: string;
}

/** Full merged-result count; unlike forecast paging, this bounded experiment can count its whole result. */
export interface WorkTaskSchedulePage {
    items: WorkTaskScheduleRow[];
    total: number;
    page: number;
    pageSize: number;
    /** Counts over the complete scoped/filter-matched result, independent of page or selected segment. */
    segmentCounts?: Record<string, number>;
}

/** Entity command ids the Work board's strategies answer; reads only, every write stays a Visit command. */
export enum WorkTaskScheduleCommandId {
    /** One row's facts for the Work quick view (`WorkTaskScheduleTaskRequest` → `WorkTaskScheduleTask`). */
    Task = 'WorkTaskScheduleTaskQuery',
}

/**
 * The Work quick view's read of one row. A forecast row's id is a one-way hash, so the row's plan and dates
 * travel with it: they bound the read to that plan and those days instead of the board's whole window.
 */
export interface WorkTaskScheduleTaskRequest {
    /** The row's `_id` (`occurrenceId:taskId`); absent when the row is found by its task, plan and dates alone. */
    _id?: string;
    taskId: string;
    servicePlanId: string;
    /** The row's scheduled local date (YYYY-MM-DD). */
    date: string;
    /** The local date a move put the work on, when it differs from `date`. */
    rescheduledDate?: string;
    timezone?: string;
}

/** The Visit a Work row sits on, as the quick view shows it: when, who and its Job, and whether it can be skipped or restored. */
export interface WorkTaskScheduleTaskVisit extends Pick<VisitBrowseRow, '_id' | 'visitId' | 'number' | 'displayStatus' | 'timezone' | 'localDate' | 'startDate' | 'endDate' | 'allDay' | 'windowStart' | 'windowEnd' | 'complianceWindowLabel' | 'resources' | 'contractor' | 'assignment' | 'bookings' | 'job' | 'jobSummary'> {
    /** `visitBrowseCapabilities(visit).skip`: a live Job-less Visit, booked or forecast. */
    canSkip: boolean;
    /** `visitBrowseCapabilities(visit).restore`: a stored Visit that was skipped. */
    canRestore: boolean;
}

/** One task row's facts beyond the board row, answered by `WorkTaskScheduleCommandId.Task`. */
export interface WorkTaskScheduleTask {
    /** The row exactly as the board reads it, re-read so the pane shows current approval and schedule state. */
    row: WorkTaskScheduleRow;
    description?: string;
    /** The standing task's subtasks, in sibling order. */
    subtasks: Entity[];
    /** The standing task's estimated minutes. */
    duration?: number;
    /** The standing task's schedule anchor (YYYY-MM-DD); absent when unanchored. */
    startsOn?: string;
    /** The local date the task was last completed. */
    lastCompleted?: string;
    /**
     * The last local date a materialised schedule is forecast through; past it the calendar says "not forecast
     * yet" rather than "nothing scheduled". Absent for class schedules, which the engine answers over any window.
     */
    projectedUntil?: string;
    /** Absent when the Visit can no longer be read (a forecast that is no longer due). */
    visit?: WorkTaskScheduleTaskVisit;
    /** The other top-level tasks on the same Visit. */
    siblings: Pick<VisitBrowseTask, '_id' | 'name' | 'visitTaskId'>[];
    /** Who approved this task and when, and whether it changed since; absent while unapproved. */
    approval?: VisitBrowseTask['approval'];
    /** The task has a stored Visit task row, so it can have a history; a forecast has none. */
    stored: boolean;
    /** The stored task row's revision, which a Reset freezes; absent while the task is forecast. */
    revision?: number;
    /**
     * The row's approvals and withdrawals, oldest first. Approval never writes the row's movement chain, so the
     * History tab reads these beside it; empty while the task is forecast.
     */
    approvalHistory: WorkTaskApprovalStep[];
}

/** One approval decision on a task row as the Work History tab shows it: who approved or withdrew it, and when. */
export interface WorkTaskApprovalStep {
    kind: 'Approved' | 'Withdrawn';
    /** Absent only when the decision's revision can no longer be read. */
    by?: Entity;
    at: Date | string;
    /** A withdrawal's reason; an approval has none. */
    reason?: string;
}
